import { Ref, ref, shallowRef } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { PrintPluginStatus } from '@/modules/PrintService';
import { IPrinter } from '@/interfaces/IPrinter';
import {
  IPrintingStrategy,
  IPrintRawBytesParams,
  usePrintingStore,
} from '../use-printing';
import {
  PrintBridgeClient,
  PrintBridgeError,
  PrintBridgeRequestError,
} from '@/lib/print-bridge/print-bridge-client';
import { usePrintHealthStore } from '@/stores/print-health.store';
import { mapBridgeStatusToUnified } from '@/lib/print/print-health.utils';
import { IDetectedBridge } from './use-plugin-detection';
import {
  BridgeDiscoveryMode,
  BridgeJobState,
  BridgePrinterStatus,
  IAddManualPrinterParams,
  IBridgeError,
  IBridgePrinter,
  IConfigChangedPayload,
  IConfigGetResult,
  IConfigSetParams,
  IConfigSetResult,
  IDiscoveryFinishedPayload,
  IDiscoveryFoundPayload,
  IHelloPayload,
  IJobRecord,
  IPrinterStatusPayload,
  IPrintSubmitParams,
  IUpdateStatePayload,
} from '@/interfaces/print-bridge-protocol';

/**
 * Per-attempt wait for a terminal job.update after print.submit. Must exceed
 * a single in-flight job's worst-case decision time on the bridge — the
 * drivers' 30s watchdogs (JOB_STUCK_TIMEOUT_MS in the spooler driver,
 * SEND_WATCHDOG_MS in the TCP driver) — plus margin. Jobs queued BEHIND a
 * stuck job on the same printer's FIFO can still legitimately exceed it and
 * resolve 'unknown' by design (items marked, Jobs tab as the human check).
 */
const SUBMIT_AWAIT_TIMEOUT_MS = 40000;
/** Re-run delay when the ready sequence fails with the socket still open. */
const READY_RETRY_DELAY_MS = 5000;
/** Delays for the internal safe auto-retry (up to 2 extra attempts). */
const SAFE_RETRY_DELAYS_MS = [1000, 3000];
/** Session cache of submitted bytes, for "Reimprimir" without regeneration. */
const JOB_BYTES_CACHE_MAX = 50;
const JOBS_LIST_MAX = 50;

const TERMINAL_STATES: BridgeJobState[] = ['done', 'failed', 'unknown'];

function isTerminal(state: BridgeJobState): boolean {
  return TERMINAL_STATES.includes(state);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface IPendingSubmit {
  params: IPrintSubmitParams;
  resolve: (record: IJobRecord) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout> | null;
  /**
   * Key under which the entry currently lives in pendingSubmits: the
   * submitted jobId, or — after a dedupeKey collapse — the jobId of the
   * earlier in-flight job whose terminal update settles this waiter.
   */
  key: string;
  /**
   * Whether a print.submit for this entry was ever handed to a connected
   * socket. While false the bytes provably never left the browser, so a
   * timeout is retry-SAFE instead of unknown.
   */
  deliveryAttempted: boolean;
}

interface ICachedJobBytes {
  base64: string;
  printerRef: string;
  meta?: IPrintSubmitParams['meta'];
}

/** Extra surface the new V2 UI and the v2 settings store consume. */
export interface IBridgePrintingStrategy extends IPrintingStrategy {
  /** Local WS port the bridge was detected on (diagnostics display). */
  port: number;
  identity: Ref<IHelloPayload | null>;
  bridgePrinters: Ref<IBridgePrinter[]>;
  /** Read-model of recent jobs (seeded by jobs.recent, live via job.update). */
  jobs: Ref<IJobRecord[]>;
  updateState: Ref<IUpdateStatePayload | null>;
  disconnect: () => void;
  /**
   * Async hook run during the connect sequence, after hello + printers.list
   * and BEFORE status flips to connected — the v2 store registers its config
   * hydration here (B3), so pipeline-ready never fires on unhydrated
   * assignments.
   */
  setReadyHook: (hook: () => Promise<void>) => void;
  refreshPrinters: () => Promise<IBridgePrinter[]>;
  addManualPrinter: (
    params: IAddManualPrinterParams
  ) => Promise<IBridgePrinter>;
  removePrinter: (printerRef: string) => Promise<void>;
  probePrinter: (
    printerRef: string
  ) => Promise<{ status: BridgePrinterStatus; statusDetail?: string[] }>;
  startDiscovery: (modes: BridgeDiscoveryMode[]) => Promise<string>;
  onDiscoveryFound: (
    cb: (payload: IDiscoveryFoundPayload) => void
  ) => () => void;
  onDiscoveryFinished: (
    cb: (payload: IDiscoveryFinishedPayload) => void
  ) => () => void;
  testPrint: (printerRef: string) => Promise<{ jobId: string }>;
  /** Resend a session-cached job's bytes as a NEW job (no dedupeKey). */
  resubmitJob: (jobId: string, printerRef?: string) => Promise<void>;
  hasJobBytes: (jobId: string) => boolean;
  /** Jobs submitted with a dedupeKey (auto-print path) this session. */
  isAutoPrintJob: (jobId: string) => boolean;
  diagSnapshot: () => Promise<unknown>;
  diagLogsTail: (lines?: number) => Promise<string>;
  checkForUpdate: () => Promise<void>;
  applyUpdate: () => Promise<void>;
  setInstanceName: (name: string) => Promise<void>;
  configGet: () => Promise<IConfigGetResult>;
  configSet: (params: IConfigSetParams) => Promise<IConfigSetResult>;
  onConfigChanged: (cb: (payload: IConfigChangedPayload) => void) => () => void;
}

export function printingStrategyBridge(
  detected: IDetectedBridge
): IBridgePrintingStrategy {
  const client = new PrintBridgeClient({
    url: `ws://127.0.0.1:${detected.port}${detected.identity.wsPath}`,
  });

  const status = ref<PrintPluginStatus>(PrintPluginStatus.Disconnected);
  const identity = shallowRef<IHelloPayload | null>(null);
  const bridgePrinters = ref<IBridgePrinter[]>([]);
  const jobs = ref<IJobRecord[]>([]);
  const updateState = ref<IUpdateStatePayload | null>(null);

  let printingStore: ReturnType<typeof usePrintingStore> | null = null;
  let readyHook: (() => Promise<void>) | null = null;
  let started = false;
  /** Guards overlapping ready sequences across quick reconnects. */
  let readySeq = 0;

  const pendingSubmits = new Map<string, IPendingSubmit>();
  const jobBytesCache = new Map<string, ICachedJobBytes>();
  const autoPrintJobIds = new Set<string>();

  function setStore(store: ReturnType<typeof usePrintingStore>) {
    printingStore = store;
  }

  // #region unified printer health (pushed INTO the print-health store)

  /** Full replace: printers.list results and printers.changed events. */
  function pushHealthMap(printers: IBridgePrinter[]) {
    const map: Record<string, ReturnType<typeof mapBridgeStatusToUnified>> =
      {};
    for (const printer of printers) {
      map[printer.ref] = mapBridgeStatusToUnified(printer.status);
    }
    usePrintHealthStore().setPrinterHealthMap(map);
  }

  /** Single update: printer.status events, job outcomes, manual probes. */
  function pushHealthEntry(ref: string, status: BridgePrinterStatus) {
    usePrintHealthStore().setPrinterHealthEntry(
      ref,
      mapBridgeStatusToUnified(status)
    );
  }

  // #endregion

  function setReadyHook(hook: () => Promise<void>) {
    readyHook = hook;
  }

  // #region connection

  function connect() {
    started = true;
    if (status.value === PrintPluginStatus.Disconnected) {
      status.value = PrintPluginStatus.Connecting;
    }
    client.connect();
  }

  function disconnect() {
    started = false;
    clearReadyRetryTimer();
    client.disconnect();
    status.value = PrintPluginStatus.Disconnected;
  }

  client.onStatusChange((clientStatus) => {
    if (!started) return;
    if (clientStatus === 'disconnected') {
      // A drop is recovered by the hello of the next connection.
      clearReadyRetryTimer();
      status.value = PrintPluginStatus.Disconnected;
    } else if (clientStatus === 'connecting') {
      status.value = PrintPluginStatus.Connecting;
    }
    // 'connected' stays Connecting until the ready sequence completes.
  });

  client.on('hello', (payload) => {
    identity.value = payload as IHelloPayload;
    printingStore?.setPluginVersion(identity.value.version);
    void runReadySequence();
  });

  let readyRetryTimer: ReturnType<typeof setTimeout> | null = null;

  function clearReadyRetryTimer() {
    if (readyRetryTimer) {
      clearTimeout(readyRetryTimer);
      readyRetryTimer = null;
    }
  }

  async function runReadySequence() {
    const seq = ++readySeq;
    clearReadyRetryTimer();

    try {
      const { printers } = await client.request<{ printers: IBridgePrinter[] }>(
        'printers.list'
      );
      if (seq !== readySeq) return;
      bridgePrinters.value = printers;
      pushHealthMap(printers);

      const recent = await client.request<IJobRecord[]>('jobs.recent', {
        limit: JOBS_LIST_MAX,
      });
      if (seq !== readySeq) return;
      seedJobs(recent);

      if (readyHook) {
        await readyHook();
        if (seq !== readySeq) return;
      }

      status.value = PrintPluginStatus.Connected;
      resendPendingSubmits();
    } catch (e) {
      console.error('[PrintBridge] ready sequence failed', e);
      // A drop mid-sequence rejects the in-flight request and the next hello
      // re-runs the sequence. But a failure with the socket still OPEN (e.g.
      // a request timeout against a wedged spooler) has no such trigger — a
      // scheduled retry is the only way out of the Connecting limbo.
      if (seq === readySeq && started && client.status === 'connected') {
        readyRetryTimer = setTimeout(() => {
          readyRetryTimer = null;
          void runReadySequence();
        }, READY_RETRY_DELAY_MS);
      }
    }
  }

  // #endregion

  // #region server events

  client.on('printers.changed', (payload) => {
    const { printers } = payload as { printers: IBridgePrinter[] };
    bridgePrinters.value = printers;
    pushHealthMap(printers);
  });

  client.on('printer.status', (payload) => {
    const statusPayload = payload as IPrinterStatusPayload;
    const printer = bridgePrinters.value.find(
      (p) => p.ref === statusPayload.ref
    );
    if (!printer) return;
    printer.status = statusPayload.status;
    printer.statusDetail = statusPayload.statusDetail;
    printer.lastSeenAt = statusPayload.at;
    pushHealthEntry(statusPayload.ref, statusPayload.status);
  });

  client.on('job.update', (payload) => {
    const record = payload as IJobRecord;
    upsertJob(record);

    if (!isTerminal(record.state)) return;
    const pending = pendingSubmits.get(record.jobId);
    if (pending) settleSubmit(record.jobId, record);
  });

  client.on('update.state', (payload) => {
    updateState.value = payload as IUpdateStatePayload;
  });

  const configChangedListeners = new Set<(p: IConfigChangedPayload) => void>();
  client.on('config.changed', (payload) => {
    for (const cb of [...configChangedListeners]) {
      cb(payload as IConfigChangedPayload);
    }
  });

  const discoveryFoundListeners = new Set<
    (p: IDiscoveryFoundPayload) => void
  >();
  const discoveryFinishedListeners = new Set<
    (p: IDiscoveryFinishedPayload) => void
  >();
  client.on('discovery.printerFound', (payload) => {
    for (const cb of [...discoveryFoundListeners]) {
      cb(payload as IDiscoveryFoundPayload);
    }
  });
  client.on('discovery.finished', (payload) => {
    for (const cb of [...discoveryFinishedListeners]) {
      cb(payload as IDiscoveryFinishedPayload);
    }
  });

  // #endregion

  // #region jobs read-model

  function seedJobs(recent: IJobRecord[]) {
    const sorted = [...recent].sort((a, b) => b.acceptedAt - a.acceptedAt);
    jobs.value = sorted.slice(0, JOBS_LIST_MAX);
  }

  function upsertJob(record: IJobRecord) {
    const index = jobs.value.findIndex((j) => j.jobId === record.jobId);
    if (index >= 0) {
      jobs.value.splice(index, 1, record);
    } else {
      jobs.value.unshift(record);
      if (jobs.value.length > JOBS_LIST_MAX) {
        jobs.value.splice(JOBS_LIST_MAX);
      }
    }
  }

  function cacheJobBytes(jobId: string, entry: ICachedJobBytes) {
    jobBytesCache.set(jobId, entry);
    if (jobBytesCache.size > JOB_BYTES_CACHE_MAX) {
      const oldest = jobBytesCache.keys().next().value;
      if (oldest) jobBytesCache.delete(oldest);
    }
  }

  function hasJobBytes(jobId: string): boolean {
    return jobBytesCache.has(jobId);
  }

  function isAutoPrintJob(jobId: string): boolean {
    return autoPrintJobIds.has(jobId);
  }

  // #endregion

  // #region print submission

  function submitAndAwait(params: IPrintSubmitParams): Promise<IJobRecord> {
    return new Promise<IJobRecord>((resolve, reject) => {
      const entry: IPendingSubmit = {
        params,
        resolve,
        reject,
        timer: null,
        key: params.jobId,
        deliveryAttempted: false,
      };
      pendingSubmits.set(entry.key, entry);
      armSubmitTimeout(entry);
      void sendSubmit(entry);
    });
  }

  function armSubmitTimeout(entry: IPendingSubmit) {
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = setTimeout(() => {
      if (!entry.deliveryAttempted) {
        // The submit never reached a connected socket: the bytes provably
        // never left the browser, so retrying cannot double-print.
        settleSubmit(entry.key, undefined, {
          code: 'PB:INTERNAL',
          message: 'Sin conexión con el plugin de impresión',
          retrySafety: 'safe',
        });
        return;
      }
      // No terminal update in time: the document MAY have printed — never
      // classify as safe (an auto-retry could double-print).
      settleSubmit(entry.key, undefined, {
        code: 'PB:INTERNAL',
        message: 'No se pudo confirmar el estado de la impresión',
        retrySafety: 'unknown',
      });
    }, SUBMIT_AWAIT_TIMEOUT_MS);
  }

  async function sendSubmit(entry: IPendingSubmit) {
    // Not connected: keep the waiter undelivered — resendPendingSubmits
    // retries after the next ready sequence, and the timeout stays SAFE.
    if (client.status !== 'connected') return;
    entry.deliveryAttempted = true;

    try {
      const res = await client.request<
        IJobRecord | { jobId: string; state: BridgeJobState }
      >('print.submit', entry.params);
      // A seen jobId (re-send after reconnect) or a dedupeKey hit returns the
      // owning job's record in ANY state; terminal means it already finished.
      const record = res as IJobRecord;
      if (isTerminal(record.state)) {
        settleSubmit(entry.key, record);
        return;
      }
      if (record.jobId !== entry.key) {
        // dedupeKey collapse: our submission was mapped onto an earlier
        // in-flight job — re-key so THAT job's terminal update settles us.
        pendingSubmits.delete(entry.key);
        entry.key = record.jobId;
        pendingSubmits.set(entry.key, entry);
        // It may have gone terminal between the response and the re-key; the
        // read-model upserts every job.update, so check it.
        const known = jobs.value.find((j) => j.jobId === record.jobId);
        if (known && isTerminal(known.state)) settleSubmit(entry.key, known);
      }
    } catch (e) {
      if (e instanceof PrintBridgeError) {
        // The bridge rejected the submission (bad request, overflow, ...).
        settleSubmit(entry.key, undefined, {
          code: e.code,
          message: e.message,
          retrySafety: e.retrySafety,
        });
        return;
      }
      if (e instanceof PrintBridgeRequestError) {
        // 'disconnected': keep the waiter — on reconnect the same jobId is
        // re-sent (idempotent bridge-side). 'timeout': the per-attempt await
        // timer decides. Either way the timer bounds the wait.
        return;
      }
      console.error('[PrintBridge] print.submit failed', e);
    }
  }

  /** Re-sends every in-flight submission after a reconnect (same jobId). */
  function resendPendingSubmits() {
    for (const entry of pendingSubmits.values()) {
      armSubmitTimeout(entry);
      void sendSubmit(entry);
    }
  }

  function settleSubmit(
    jobId: string,
    record?: IJobRecord,
    error?: IBridgeError
  ) {
    const entry = pendingSubmits.get(jobId);
    if (!entry) return;
    pendingSubmits.delete(jobId);
    if (entry.timer) clearTimeout(entry.timer);

    if (record) entry.resolve(record);
    else entry.reject(new PrintBridgeError(error!));
  }

  async function printRawBytes(data: IPrintRawBytesParams): Promise<void> {
    const refCandidate = data.id ?? data.printer?.id;
    if (refCandidate === undefined || refCandidate === null) {
      throw new Error(
        'If no printer id is provided, a printer object must be provided'
      );
    }
    const printerRef = String(refCandidate);
    const context = data.printContext;

    let meta: IPrintSubmitParams['meta'];
    if (context?.kind || context?.label || context?.stationId != null) {
      meta = {
        kind: context.kind,
        label: context.label,
        stationId: context.stationId,
      };
    }

    for (let attempt = 0; ; attempt++) {
      const jobId = uuidv4();
      cacheJobBytes(jobId, { base64: data.base64, printerRef, meta });
      if (context?.dedupeKey) autoPrintJobIds.add(jobId);

      try {
        const record = await submitAndAwait({
          jobId,
          dedupeKey: context?.dedupeKey,
          printerRef,
          payload: { encoding: 'base64', data: data.base64 },
          meta,
        });

        if (record.state === 'done') return;

        throw new PrintBridgeError(
          record.error ?? {
            code: 'PB:INTERNAL',
            message: 'La impresión falló',
            retrySafety: record.state === 'unknown' ? 'unknown' : 'safe',
          }
        );
      } catch (e) {
        if (
          e instanceof PrintBridgeError &&
          e.retrySafety === 'safe' &&
          attempt < SAFE_RETRY_DELAYS_MS.length
        ) {
          await sleep(SAFE_RETRY_DELAYS_MS[attempt]);
          continue;
        }
        throw e;
      }
    }
  }

  async function resubmitJob(
    jobId: string,
    printerRef?: string
  ): Promise<void> {
    const cached = jobBytesCache.get(jobId);
    if (!cached) {
      throw new Error('No hay datos de esta impresión en la sesión actual');
    }

    const newJobId = uuidv4();
    const targetRef = printerRef ?? cached.printerRef;
    cacheJobBytes(newJobId, { ...cached, printerRef: targetRef });

    const record = await submitAndAwait({
      jobId: newJobId,
      printerRef: targetRef,
      payload: { encoding: 'base64', data: cached.base64 },
      meta: cached.meta,
    });

    if (record.state !== 'done') {
      throw new PrintBridgeError(
        record.error ?? {
          code: 'PB:INTERNAL',
          message: 'La impresión falló',
          retrySafety: record.state === 'unknown' ? 'unknown' : 'safe',
        }
      );
    }
  }

  // #endregion

  // #region strategy surface

  async function getPrinters(mode: 'usb' | 'bluetooth'): Promise<IPrinter[]> {
    const printers = await refreshPrinters();
    return printers
      .filter((p) => !p.meta.isVirtual)
      .map((p) => ({ id: p.ref, name: p.name, type: mode }));
  }

  async function refreshPrinters(): Promise<IBridgePrinter[]> {
    const { printers } = await client.request<{ printers: IBridgePrinter[] }>(
      'printers.list'
    );
    bridgePrinters.value = printers;
    pushHealthMap(printers);
    return printers;
  }

  async function addManualPrinter(
    params: IAddManualPrinterParams
  ): Promise<IBridgePrinter> {
    const printer = await client.request<IBridgePrinter>(
      'printers.addManual',
      params,
      15000
    );
    await refreshPrinters();
    return printer;
  }

  async function removePrinter(printerRef: string): Promise<void> {
    await client.request('printers.remove', { ref: printerRef });
    await refreshPrinters();
  }

  async function probePrinter(printerRef: string) {
    const result = await client.request<{
      status: BridgePrinterStatus;
      statusDetail?: string[];
    }>('printers.probe', { ref: printerRef }, 15000);
    // Manual probe feeds the health map too: an online result can fire the
    // recovery reconcile without waiting for the next 30s probe cycle.
    pushHealthEntry(printerRef, result.status);
    return result;
  }

  async function startDiscovery(modes: BridgeDiscoveryMode[]): Promise<string> {
    const { discoveryId } = await client.request<{ discoveryId: string }>(
      'discovery.start',
      { modes }
    );
    return discoveryId;
  }

  function onDiscoveryFound(cb: (payload: IDiscoveryFoundPayload) => void) {
    discoveryFoundListeners.add(cb);
    return () => {
      discoveryFoundListeners.delete(cb);
    };
  }

  function onDiscoveryFinished(
    cb: (payload: IDiscoveryFinishedPayload) => void
  ) {
    discoveryFinishedListeners.add(cb);
    return () => {
      discoveryFinishedListeners.delete(cb);
    };
  }

  function testPrint(printerRef: string) {
    return client.request<{ jobId: string }>('test.print', {
      ref: printerRef,
    });
  }

  function diagSnapshot() {
    return client.request<unknown>('diag.snapshot');
  }

  function diagLogsTail(lines = 500) {
    return client.request<string>('diag.logsTail', { lines });
  }

  async function checkForUpdate(): Promise<void> {
    await client.request('update.check');
  }

  async function applyUpdate(): Promise<void> {
    await client.request('update.apply');
  }

  async function setInstanceName(name: string): Promise<void> {
    await client.request('instance.setName', { name });
    if (identity.value) {
      identity.value = { ...identity.value, instanceName: name };
    }
  }

  function configGet() {
    return client.request<IConfigGetResult>('config.get');
  }

  function configSet(params: IConfigSetParams) {
    return client.request<IConfigSetResult>('config.set', params);
  }

  function onConfigChanged(cb: (payload: IConfigChangedPayload) => void) {
    configChangedListeners.add(cb);
    return () => {
      configChangedListeners.delete(cb);
    };
  }

  // #endregion

  return {
    port: detected.port,
    status,
    setStore,
    connect,
    disconnect,
    getPrinters,
    printRawBytes,
    identity,
    bridgePrinters,
    jobs,
    updateState,
    setReadyHook,
    refreshPrinters,
    addManualPrinter,
    removePrinter,
    probePrinter,
    startDiscovery,
    onDiscoveryFound,
    onDiscoveryFinished,
    testPrint,
    resubmitJob,
    hasJobBytes,
    isAutoPrintJob,
    diagSnapshot,
    diagLogsTail,
    checkForUpdate,
    applyUpdate,
    setInstanceName,
    configGet,
    configSet,
    onConfigChanged,
  };
}
